/**
 * FOND Mega-Nav — Desktop hover disclosure + Mobile accordion
 * No dependencies. Loaded deferred from header.liquid.
 */
(function () {
  'use strict';

  var HOVER_DELAY = 100;

  /* -----------------------------------------------------------------------
     Desktop: Hover disclosure
     ----------------------------------------------------------------------- */
  function initDesktopNav() {
    var triggers = document.querySelectorAll('[data-mega-nav-trigger]');
    var openTimeout = null;
    var closeTimeout = null;
    var currentOpen = null;

    function openDropdown(trigger) {
      clearTimeout(closeTimeout);
      if (currentOpen && currentOpen !== trigger) {
        closeDropdown(currentOpen);
      }
      trigger.setAttribute('aria-expanded', 'true');
      currentOpen = trigger;
      /* Body-level flag so CSS can dim inactive top-level parents */
      document.body.setAttribute('data-mega-nav-open', '');
    }

    function closeDropdown(trigger) {
      if (!trigger) return;
      trigger.setAttribute('aria-expanded', 'false');
      if (currentOpen === trigger) currentOpen = null;
      if (!currentOpen) {
        document.body.removeAttribute('data-mega-nav-open');
      }
    }

    function closeAll() {
      clearTimeout(openTimeout);
      clearTimeout(closeTimeout);
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      currentOpen = null;
      document.body.removeAttribute('data-mega-nav-open');
    }

    triggers.forEach(function (trigger) {
      var dropdown = trigger.querySelector('[data-mega-nav-dropdown]');

      trigger.addEventListener('mouseenter', function () {
        clearTimeout(closeTimeout);
        openTimeout = setTimeout(function () {
          openDropdown(trigger);
        }, HOVER_DELAY);
      });

      trigger.addEventListener('mouseleave', function () {
        clearTimeout(openTimeout);
        closeTimeout = setTimeout(function () {
          closeDropdown(trigger);
        }, HOVER_DELAY);
      });

      if (dropdown) {
        dropdown.addEventListener('mouseenter', function () {
          clearTimeout(closeTimeout);
        });

        dropdown.addEventListener('mouseleave', function () {
          closeTimeout = setTimeout(function () {
            closeDropdown(trigger);
          }, HOVER_DELAY);
        });
      }

      /* Keyboard support */
      var linkEl = trigger.querySelector('.mega-nav__link');
      if (linkEl) {
        linkEl.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            var isOpen = trigger.getAttribute('aria-expanded') === 'true';
            if (isOpen) {
              closeDropdown(trigger);
            } else {
              openDropdown(trigger);
              /* Focus first link in dropdown */
              var firstLink = dropdown
                ? dropdown.querySelector('.mega-nav__sublink, .mega-nav__card-link')
                : null;
              if (firstLink) firstLink.focus();
            }
          }
          if (e.key === 'Escape') {
            closeDropdown(trigger);
            linkEl.focus();
          }
        });
      }

      /* Keyboard nav within dropdown: Escape + focus trap */
      if (dropdown) {
        dropdown.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') {
            closeDropdown(trigger);
            if (linkEl) linkEl.focus();
            return;
          }

          /* Focus trap: Tab cycles within open dropdown */
          if (e.key === 'Tab') {
            var focusable = dropdown.querySelectorAll(
              'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (focusable.length === 0) return;
            var first = focusable[0];
            var last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        });
      }
    });

    /* Close on click outside */
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-mega-nav-trigger]')) {
        closeAll();
      }
    });
  }

  /* -----------------------------------------------------------------------
     Mobile: Accordion toggle (one-at-a-time)
     ----------------------------------------------------------------------- */
  function initMobileAccordion() {
    var triggers = document.querySelectorAll('[data-mega-nav-mobile-trigger]');

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        var panelId = trigger.getAttribute('aria-controls');
        var panel = document.getElementById(panelId);

        /* Close all other panels */
        triggers.forEach(function (other) {
          if (other !== trigger) {
            other.setAttribute('aria-expanded', 'false');
            var otherId = other.getAttribute('aria-controls');
            var otherPanel = document.getElementById(otherId);
            if (otherPanel) {
              otherPanel.style.maxHeight = null;
            }
          }
        });

        /* Toggle this panel */
        if (isOpen) {
          trigger.setAttribute('aria-expanded', 'false');
          if (panel) panel.style.maxHeight = null;
        } else {
          trigger.setAttribute('aria-expanded', 'true');
          if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
        }
      });
    });
  }

  /* -----------------------------------------------------------------------
     Update --fond-header-bottom CSS var to the real rendered bottom edge of
     .theme__header so the mobile drawer aligns exactly with the nav bar.
     The theme's own `--announcement-height` variable computes to 36px but
     the real announcement bar renders at 40px, so `calc(--header-height +
     --announcement-height)` is 4px short of the real header bottom. Reading
     the actual rect is the only reliable source of truth.
     ----------------------------------------------------------------------- */
  function initDrawerOffset() {
    var root = document.documentElement;

    function updateOffset() {
      var theme = document.querySelector('.theme__header');
      if (!theme) return;
      var rect = theme.getBoundingClientRect();
      /* The header is position:absolute so rect.top is the offset from the
         top of the viewport (which equals the top of the page at scrollY 0).
         Subtract 1px so the drawer covers the theme__header's translucent
         1px border-bottom (rgba(71,71,41,0.2)) — otherwise that border lets
         the banner image show through as a thin gap between the nav bar
         and the drawer. */
      var bottom = rect.top + rect.height - 1;
      root.style.setProperty('--fond-header-bottom', bottom + 'px');
    }

    updateOffset();
    window.addEventListener('resize', updateOffset);
    window.addEventListener('orientationchange', updateOffset);
    /* Also re-measure after fonts load in case the announcement text reflows. */
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(updateOffset);
    }
  }

  /* -----------------------------------------------------------------------
     Auto-close mobile drawer when viewport crosses from mobile → desktop.
     The theme has no native handler for this, so an open drawer remains
     visible (and body stays scroll-locked) after resizing to desktop.
     We invoke the theme's own close flow via a programmatic click on the
     hamburger so body scroll unlocks and `.is-open` / `aria-expanded`
     reset cleanly.
     ----------------------------------------------------------------------- */
  function initDrawerAutoClose() {
    if (!window.matchMedia) return;
    var mq = window.matchMedia('(max-width: 991px)');
    function handleBreakpointChange(e) {
      if (e.matches) return; /* still mobile, nothing to do */
      var burger = document.querySelector(
        '.header__mobile__hamburger[aria-expanded="true"]'
      );
      if (burger) burger.click();
    }
    if (mq.addEventListener) {
      mq.addEventListener('change', handleBreakpointChange);
    } else if (mq.addListener) {
      mq.addListener(handleBreakpointChange); /* Safari <14 fallback */
    }
  }

  /* -----------------------------------------------------------------------
     Init — runs once on DOMContentLoaded
     ----------------------------------------------------------------------- */
  function init() {
    initDesktopNav();
    initMobileAccordion();
    initDrawerAutoClose();
    initDrawerOffset();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
